import { supabase } from '../lib/supabase'
import { obtenerCuentasFinancieras } from './cuentasFinancierasService'

export const PAGE_SIZE_MOVIMIENTOS = 20

function aplicarFiltros(query, organizacionId, filters = {}) {
  query = query.eq('organizacion_id', organizacionId)
  if (filters.tipo) query = query.eq('tipo', filters.tipo)
  if (filters.categoriaId) query = query.eq('categoria_id', filters.categoriaId)
  if (filters.cuentaId) query = query.eq('cuenta_id', filters.cuentaId)
  if (filters.estado) query = query.eq('estado', filters.estado)
  if (filters.desde) query = query.gte('fecha', filters.desde)
  if (filters.hasta) query = query.lte('fecha', filters.hasta)
  if (filters.search?.trim()) query = query.ilike('concepto', `%${filters.search.trim()}%`)
  return query
}

export async function obtenerMovimientos({ organizacionId, page = 1, pageSize = PAGE_SIZE_MOVIMIENTOS, ...filters }) {
  const from = (page - 1) * pageSize
  let query = supabase.from('cp_movimientos_financieros')
    .select('*, cuenta:cp_cuentas_financieras(nombre), categoria:cp_categorias_financieras(nombre)', { count: 'exact' })
  query = aplicarFiltros(query, organizacionId, filters)
  const { data, count, error } = await query.order('fecha', { ascending: false }).order('created_at', { ascending: false }).range(from, from + pageSize - 1)
  if (error) throw error
  return { movimientos: data ?? [], total: count ?? 0 }
}

export async function guardarMovimiento(organizacionId, tipo, values, id = null) {
  const payload = {
    organizacion_id: organizacionId, tipo, fecha: values.fecha, cuenta_id: values.cuenta_id,
    categoria_id: values.categoria_id, monto: Number(values.monto), concepto: values.concepto.trim(),
    descripcion: values.descripcion?.trim() || null, numero_documento: values.numero_documento?.trim() || null,
    medio_pago: values.medio_pago?.trim() || null,
  }
  let query = id
    ? supabase.from('cp_movimientos_financieros').update(payload).eq('id', id).eq('organizacion_id', organizacionId).eq('estado', 'vigente')
    : supabase.from('cp_movimientos_financieros').insert(payload)
  const { data, error } = await query.select().single()
  if (error) throw error
  return data
}

export async function anularMovimiento(organizacionId, id, motivo) {
  const { data, error } = await supabase.from('cp_movimientos_financieros')
    .update({ estado: 'anulado', motivo_anulacion: motivo.trim() })
    .eq('id', id).eq('organizacion_id', organizacionId).eq('estado', 'vigente').select().single()
  if (error) throw error
  return data
}

async function obtenerMovimientosVigentes(organizacionId) {
  const { data, error } = await supabase.from('cp_movimientos_financieros')
    .select('id,cuenta_id,categoria_id,tipo,fecha,monto,concepto,estado,created_at,categoria:cp_categorias_financieras(nombre),cuenta:cp_cuentas_financieras(nombre)')
    .eq('organizacion_id', organizacionId).eq('estado', 'vigente')
  if (error) throw error
  return data ?? []
}

export async function obtenerResumenTesoreria(organizacionId, periodo) {
  const [cuentas, movimientos, ultimos] = await Promise.all([
    obtenerCuentasFinancieras(organizacionId),
    obtenerMovimientosVigentes(organizacionId),
    obtenerMovimientos({ organizacionId, page: 1, pageSize: 10 }),
  ])
  const inicio = `${periodo}-01`
  const [year, month] = periodo.split('-').map(Number)
  const fin = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10)
  const porCuenta = cuentas.map((cuenta) => {
    const delta = movimientos.filter((m) => m.cuenta_id === cuenta.id).reduce((sum, m) => sum + (m.tipo === 'ingreso' ? Number(m.monto) : -Number(m.monto)), 0)
    return { ...cuenta, saldo_actual: Number(cuenta.saldo_inicial) + delta }
  })
  const anteriores = movimientos.filter((m) => m.fecha < inicio)
  const delMes = movimientos.filter((m) => m.fecha >= inicio && m.fecha <= fin)
  const signed = (list) => list.reduce((sum, m) => sum + (m.tipo === 'ingreso' ? Number(m.monto) : -Number(m.monto)), 0)
  const saldoBase = cuentas.reduce((sum, c) => sum + Number(c.saldo_inicial), 0)
  const ingresos = delMes.filter((m) => m.tipo === 'ingreso').reduce((s, m) => s + Number(m.monto), 0)
  const gastos = delMes.filter((m) => m.tipo === 'gasto').reduce((s, m) => s + Number(m.monto), 0)
  const desglose = (tipo) => Object.values(delMes.filter((m) => m.tipo === tipo).reduce((acc, m) => {
    const key = m.categoria_id
    acc[key] ||= { id: key, nombre: m.categoria?.nombre || 'Sin categoría', total: 0 }
    acc[key].total += Number(m.monto)
    return acc
  }, {})).sort((a, b) => b.total - a.total)
  const saldoInicialPeriodo = saldoBase + signed(anteriores)
  return {
    cuentas: porCuenta, ultimos: ultimos.movimientos,
    saldoActual: porCuenta.reduce((s, c) => s + c.saldo_actual, 0),
    ingresos, gastos, resultado: ingresos - gastos,
    mensual: { saldoInicial: saldoInicialPeriodo, ingresos, gastos, resultado: ingresos - gastos, saldoFinal: saldoInicialPeriodo + ingresos - gastos, ingresosCategorias: desglose('ingreso'), gastosCategorias: desglose('gasto') },
  }
}
